import ax from "../../config/axios";
import { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import CustomContextHeatmap from "./CustomContextHeatmap";
import "bootstrap-icons/font/bootstrap-icons.css";
import styles from "./Correlation.module.css";

export type CorrelationData = {
  filename: string;
  days: number;
  x: string[];
  y: string[];
  z: number[][];
};

function Correlation() {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [correlationData, setCorrelationData] = useState<
    CorrelationData[] | null
  >(null);
  const [selectedPoints, setSelectedPoints] = useState<{
    x: string;
    y: string;
  } | null>(null);
  //RootSymbol = keyof typeof rootLUT
  //Using that type in your state: selectedPoints: { x: RootSymbol; y: RootSymbol }
  const [historicalCorrelationPair, setHistoricalCorrelationPair] = useState<
    { date: string; correl: number; averaging_days: number }[] | null
  >(null);
  const [adrRatioPairs, setAdrRatioPairs] = useState<{
    data: {
      [key: string]: {
        [key: string]: {
          ratio_str: string;
          ideal_ratio_str: string;
          abs_log_err: number;
          zn_risk: string;
        };
      };
    };
    last_update: string;
  } | null>(null);
  const plotRef = useRef<any>(null);


  const rootLUT = {
    ZN: { name: "10-Year T-Note", exchange: "CBOT" },
    ZT: { name: "2-Year T-Note", exchange: "CBOT" },
    ZF: { name: "5-Year T-Note", exchange: "CBOT" },
    TN: { name: "Ultra 10-Year T-Note", exchange: "CBOT" },
    ZB: { name: "30-Year T-Bond", exchange: "CBOT" },
    UD: { name: "Ultra T-Bond", exchange: "CBOT" },
    G: { name: "10-Year Long Gilt", exchange: "LIFFE" },
    HF: { name: "Euro Schatz", exchange: "EUREX" },
    HR: { name: "Euro Bobl", exchange: "EUREX" },
    GG: { name: "Euro Bund", exchange: "EUREX" },
    GX: { name: "Euro Buxl", exchange: "EUREX" },
    IA: { name: "Euro BTP Short-Term", exchange: "EUREX" },
    II: { name: "Euro BTP Long-Term", exchange: "EUREX" },
    FN: { name: "Euro OAT Long-Term", exchange: "EUREX" },
    CH: { name: "Canadian 2-Year", exchange: "MNTRL" },
    GM: { name: "Canadian 5-Year", exchange: "MNTRL" },
    CG: { name: "Canadian 10-Year", exchange: "MNTRL" },
    YT: { name: "SFE 3-Year T-Bond", exchange: "SFE" },
    YU: { name: "SFE 10-Year T-Bond", exchange: "SFE" },
    ES: { name: "S&P 500 E-Mini", exchange: "GBLX" },
    TP: { name: "S&P TSX 60", exchange: "MNTRL" },
    DY: { name: "DAX Index", exchange: "EUREX" },
    X: { name: "FTSE 100", exchange: "LIFFE" },
    DE: { name: "MSCI EMI Index", exchange: "ICEUS" },
    QR: { name: "Russell 2000 E-Mini", exchange: "GBLX" },
    NQ: { name: "Nasdaq 100 E-Mini", exchange: "GBLX" },
    YM: { name: "Dow Futures Mini", exchange: "CBOTM" },
    NY: { name: "Nikkei 225", exchange: "CME" },
    DX: { name: "U.S. Dollar Index", exchange: "ICEUS" },
    VI: { name: "S&P 500 VIX", exchange: "CFE" },
    E6: { name: "Euro FX", exchange: "CME" },
    B6: { name: "British Pound", exchange: "CME" },
    A6: { name: "Australian Dollar", exchange: "CME" },
    N6: { name: "New Zealand Dollar", exchange: "CME" },
    D6: { name: "Canadian Dollar", exchange: "CME" },
    J6: { name: "Japanese Yen", exchange: "CME" },
    M6: { name: "Mexican Peso", exchange: "CME" },
    GC: { name: "Gold", exchange: "COMEX" },
    SI: { name: "Silver", exchange: "COMEX" },
    HG: { name: "High Grade Copper", exchange: "COMEX" },
    PL: { name: "Platinum", exchange: "NYMEX" },
    PA: { name: "Palladium", exchange: "NYMEX" },
    AL: { name: "Aluminum", exchange: "COMEX" },
    CL: { name: "Crude Oil WTI", exchange: "NYMEX" },
    RB: { name: "Gasoline RBOB", exchange: "NYMEX" },
    HO: { name: "ULSD NY Harbor", exchange: "NYMEX" },
    NG: { name: "Natural Gas", exchange: "NYMEX" },
    BA: { name: "Bitcoin Micro", exchange: "GBLX" },
    ZS: { name: "Soybean", exchange: "CBOT" },
    ZM: { name: "Soybean Meal", exchange: "CBOT" },
    ZL: { name: "Soybean Oil", exchange: "CBOT" },
    ZC: { name: "Corn", exchange: "CBOT" },
    ZW: { name: "Wheat", exchange: "CBOT" },
    LE: { name: "Live Cattle", exchange: "CME" },
    HE: { name: "Lean Hogs", exchange: "CME" },
    GF: { name: "Feeder Cattle", exchange: "CME" },
    DL: { name: "Class III Milk", exchange: "CME" },
    SB: { name: "Sugar #11", exchange: "ICEUS" },
    KC: { name: "Coffee", exchange: "ICEUS" },
    CC: { name: "Cocoa", exchange: "ICEUS" },
    CT: { name: "Cotton #2", exchange: "ICEUS" },
    OJ: { name: "Orange Juice", exchange: "ICEUS" },
    RM: { name: "Robusta Coffee 10-T", exchange: "ICE" },
    CA: { name: "Cocoa #7", exchange: "ICE" },
  };

  // Dimension calculations for the root names display
  const ITEM_HEIGHT = 28;
  const COLUMN_WIDTH = 250;
  const COLUMN_GAP = 20;
  const DESIRED_COLUMNS = 4;
  const CARD_PADDING = 40;

  const itemCount = Object.keys(rootLUT).length;
  const itemsPerColumn = Math.ceil(itemCount / DESIRED_COLUMNS);
  const optimalHeight = `${itemsPerColumn * ITEM_HEIGHT}px`;
  const cardWidth = `${
    COLUMN_WIDTH * DESIRED_COLUMNS +
    COLUMN_GAP * (DESIRED_COLUMNS - 1) +
    CARD_PADDING
  }px`;

  useEffect(() => {
    ax.get("data/get-daily-correlations").then((response) => {
      const data: CorrelationData[] = response.data;
      setCorrelationData(data);
      setSelectedFile(data[0].filename);
    });
  }, []);

  useEffect(() => {
    ax.get("/data/adr-ratio-pairs").then((response) => {
      setAdrRatioPairs(response.data);
    });
  }, []);

  const processCorrelationRanking = (
    data: CorrelationData,
    acending: boolean = false,
  ) => {

    const RANK_PAIR_EXCLUSIONS: Record<string, string[]> = {
      "CG": ["CH", "GM"],
      "CA": ["CC"],
      "GG": ["GX", "HF", "HR"],
      "II": ["IA", "FN"],
      "KC": ["RM"],
      "TN": ["UD", "ZB", "ZF", "ZN", "ZT"],
      "TY": ["YU"],
      "NQ": ["YM", "ES", "RTY"],
      "YM": ["NQ", "ES", "RTY"],
      "ES": ["NQ", "YM", "RTY"],
      "RTY": ["NQ", "YM", "ES"],
      "A6": ["N6"],
      "S6": ["E6"],
      "FX": ["DY"]
    }

    const pairs = [];
    for (let i = 0; i < data.z.length; i++) {
      for (let j = i + 1; j < data.z[i].length; j++) {
        //i+1 to avoid duplicate pairs and self-pairs

        if (RANK_PAIR_EXCLUSIONS[data.x[i]]?.includes(data.y[j])) {
          continue;
        }

        if (RANK_PAIR_EXCLUSIONS[data.y[j]]?.includes(data.x[i])) {
          continue;
        }

        pairs.push({
          root_1: data.x[i],
          root_2: data.y[j],
          correl: data.z[i][j],
        });
      }
    }
    return pairs.sort((a, b) =>
      acending
        ? Math.abs(a.correl) - Math.abs(b.correl)
        : Math.abs(b.correl) - Math.abs(a.correl),
    );
  };

  useEffect(() => {
    //resize the heatmap when the selected points change
    // Add a small delay to let the flex transition complete
    const timer = setTimeout(() => {
      if (plotRef.current) {
        window.dispatchEvent(new Event("resize"));
      }
    }, 300); // Match the transition duration

    //make an api call to get the historical correlations
    if (selectedPoints) {
      ax.get("/data/historical-correlations", {
        params: {
          root_1: selectedPoints.x,
          root_2: selectedPoints.y,
        },
      }).then((response) => {
        setHistoricalCorrelationPair(response.data);
      });
    }

    return () => clearTimeout(timer);
  }, [selectedPoints]);

  const handleInspectButton = (points: any) => {
    console.log(points);
    setSelectedPoints(points);
  };

  const correlationRanking = useMemo(
    () => correlationData?.map((data) => processCorrelationRanking(data)),
    [correlationData],
  );

  const adrLastUpdate = useMemo(() => {
    const d = adrRatioPairs?.last_update;
    if (!d) return "N/A";
    if (/^\d{8}$/.test(d)) {
      return `${d.slice(4, 6)}/${d.slice(6, 8)}/${d.slice(0, 4)}`;
    }
    return d;
  }, [adrRatioPairs?.last_update]);

  return (
    <div className="w-100 h-100 d-flex flex-column pb-2">
      <div
        className={`d-flex flex-row flex-grow-1 ${
          selectedPoints ? "gap-1" : ""
        }`}
      >
        <div
          className="card w-100 h-100 p-1 px-3 flex-grow-1"
          style={{
            transition: "flex 0.3s ease-in-out",
            flex: selectedPoints ? "1 1 50%" : "1 1 100%",
            overflow: "hidden",
          }}
        >
          <div className="card-body d-flex flex-column h-100">
            <div className="w-100 d-flex justify-content-between">
              <div className="dropdown" style={{ minWidth: 320 }}>
                <button
                  className="btn btn-secondary dropdown-toggle w-100 d-flex align-items-center position-relative"
                  data-bs-toggle="dropdown"
                  type="button"
                >
                  <span className="position-absolute start-50 translate-middle-x">
                    {selectedFile ?? "Select a Correlation Chart"}
                  </span>
                  <span className="ms-auto invisible">▼</span>{" "}
                </button>
                <div className="dropdown-menu dropdown-menu-end">
                  {correlationData?.map((item) => (
                    <button
                      key={item.filename}
                      className="dropdown-item"
                      onClick={() => setSelectedFile(item.filename)}
                    >
                      {item.filename}
                    </button>
                  ))}
                </div>
              </div>

              {/* hover buttons */}
              <div className="d-flex gap-4 me-3 order-lg-3 order-2">
                {/* info hover Icon */}
                <div className=" d-inline-block">
                  {/* Icon */}
                  <div
                    className={`${styles.info_hover_trigger} position-relative`}
                  >
                    <i
                      className="bi bi-info-circle-fill text-secondary"
                      style={{ fontSize: 50 }}
                    />

                    {/* Hover Card */}
                    <div
                      className={`${styles.info_hover_card} position-absolute p-1 pt-4`}
                      style={{
                        top: "75%",
                        left: "120%",
                        transform: "translateX(calc(-100%))",
                        width: cardWidth,
                        zIndex: 1000,
                      }}
                    >
                      <div className=" card border rounded shadow">
                        {/* Notch */}
                        <div
                          className="position-absolute"
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: "#f8f8f8",
                            borderLeft: "1px solid #dee2e6",
                            borderTop: "1px solid #dee2e6",
                            transform: "translate(-200%, -50%) rotate(45deg)",
                            top: 0,
                            left: "100%",
                            zIndex: 1,
                          }}
                        />

                        {/* Card Content */}
                        <div className="card-header fw-semibold text-center">
                          Barchart Root Names
                        </div>
                        <div className="card-body">
                          <ul
                            className="d-flex flex-column flex-wrap p-0 m-0"
                            style={{
                              listStyle: "none",
                              height: optimalHeight,
                              maxHeight: "60vh",
                              columnGap: `${COLUMN_GAP}px`,
                              overflow: "clip",
                              width: "100%",
                            }}
                          >
                            {Object.entries(rootLUT)
                              .sort(([keyA], [keyB]) =>
                                keyA.localeCompare(keyB),
                              )
                              .map(([key, obj]) => (
                                <li
                                  title={`Exchange: ${obj.exchange}`}
                                  key={key}
                                  className="d-flex"
                                  style={{
                                    width: `${COLUMN_WIDTH}px`,
                                    whiteSpace: "nowrap",
                                    padding: "2px 0",
                                    height: `${ITEM_HEIGHT}px`,
                                  }}
                                >
                                  <span className="me-0">•</span>
                                  <div className="d-flex w-100">
                                    <span
                                      className="text-end fw-bold me-1"
                                      style={{ flex: "0 0 40px" }}
                                    >
                                      {key} :
                                    </span>
                                    <span className="text-start text-truncate">
                                      {obj.name}
                                    </span>
                                  </div>
                                </li>
                              ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* question hover Icon */}
                <div className=" d-inline-block">
                  {/* Icon */}
                  <div
                    className={`${styles.info_hover_trigger} position-relative`}
                  >
                    <i
                      className="bi bi-question-circle-fill text-secondary"
                      style={{ fontSize: 50 }}
                    />

                    {/* Hover Card */}
                    <div
                      className={`${styles.info_hover_card} position-absolute p-1 pt-4 `}
                      style={{
                        top: "75%",
                        left: "120%",
                        transform: "translateX(calc(-100%))",
                        width: "420px",
                        zIndex: 1000,
                      }}
                    >
                      <div className="position-relative bg-light border rounded shadow p-3">
                        {/* Notch */}
                        <div
                          className="position-absolute bg-light"
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: "white",
                            borderLeft: "1px solid #dee2e6",
                            borderTop: "1px solid #dee2e6",
                            transform: "translate(-200%, -50%) rotate(45deg)",
                            top: 0, // Ensures it's placed at the top edge
                            left: "100%",
                            zIndex: 1,
                          }}
                        />

                        {/* Card Content */}
                        <div className="mt-2">
                          <strong>What's New:</strong>
                          <p className="mb-0">
                            Right-Click on a point on the grid to inspect
                            further. (More features coming soon...)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="d-flex  align-content-center flex-grow-1 pe-5"
              style={{ overflowX: "scroll" }}
            >
              <div
                className=" h-100  align-items-center me-5 gap-3 py-5"
                style={{ display: selectedPoints ? "none" : "flex" }}
              >
                <div className="card" style={{ width: 300, height: "70vh" }}>
                  <div
                    className="card-header text-center fw-semibold"
                    style={{ fontSize: 25 }}
                  >
                    5 DAY
                  </div>
                  <div
                    className="card-body py-2 px-0"
                    style={{ height: "calc(100% - 60px)" }}
                  >
                    <div
                      className="overflow-auto h-100"
                      style={{ padding: "1rem" }}
                    >
                      {correlationRanking?.[0]
                        ?.slice(0, 100)
                        .map((item, rank_idx) => (
                          <button
                            key={item.root_1 + "_" + item.root_2}
                            className="btn btn-outline-secondary  btn-sm w-100 mb-1 d-flex justify-content-between align-items-center"
                            style={{ fontSize: "1.3rem" }}
                            onClick={() => {
                              setSelectedPoints({
                                x: item.root_1,
                                y: item.root_2,
                              });
                            }}
                          >
                            <span className="fw-bold">
                              {rank_idx + 1}.{"  "}
                              {item.root_1} : {item.root_2}
                            </span>
                            <span className="fw-semibold">
                              {item.correl.toFixed(4)}
                            </span>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="card" style={{ width: 300, height: "70vh" }}>
                  <div
                    className="card-header text-center fw-semibold"
                    style={{ fontSize: 25 }}
                  >
                    10 DAY
                  </div>
                  <div
                    className="card-body py-2 px-0"
                    style={{ height: "calc(100% - 60px)" }}
                  >
                    <div
                      className="overflow-auto h-100"
                      style={{ padding: "1rem" }}
                    >
                      {correlationRanking?.[1]
                        ?.slice(0, 100)
                        .map((item, rank_idx) => (
                          <button
                            key={item.root_1 + '_' + item.root_2}
                            className="btn btn-outline-secondary btn-sm w-100 mb-1 d-flex justify-content-between align-items-center"
                            style={{ fontSize: "1.3rem" }}
                            onClick={() => {
                              setSelectedPoints({
                                x: item.root_1,
                                y: item.root_2,
                              });
                            }}
                          >
                            <span className="fw-bold">
                              {rank_idx + 1}.{"  "}
                              {item.root_1} : {item.root_2}
                            </span>
                            <span className="fw-semibold">
                              {item.correl.toFixed(4)}
                            </span>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="card" style={{ width: 300, height: "70vh" }}>
                  <div
                    className="card-header text-center fw-semibold"
                    style={{ fontSize: 25 }}
                  >
                    20 DAY
                  </div>
                  <div
                    className="card-body py-2 px-0"
                    style={{ height: "calc(100% - 60px)" }}
                  >
                    <div
                      className="overflow-auto h-100"
                      style={{ padding: "1rem" }}
                    >
                      {correlationRanking?.[2]
                        ?.slice(0, 100)
                        .map((item, rank_idx) => (
                          <button
                            key={item.root_1 + item.root_2}
                            className="btn btn-outline-secondary btn-sm w-100 mb-1 d-flex justify-content-between align-items-center"
                            style={{ fontSize: "1.3rem" }}
                            onClick={() => {
                              setSelectedPoints({
                                x: item.root_1,
                                y: item.root_2,
                              });
                            }}
                          >
                            <span className="fw-bold">
                              {rank_idx + 1}.{"  "}
                              {item.root_1} : {item.root_2}
                            </span>
                            <span className="fw-semibold">
                              {item.correl.toFixed(4)}
                            </span>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              {selectedFile && correlationData && (
                <CustomContextHeatmap
                  ref={plotRef}
                  data={correlationData}
                  selectedFile={selectedFile}
                  selectedPoint={selectedPoints}
                  onInspectButton={handleInspectButton}
                />
              )}
            </div>
          </div>
        </div>
        <div
          className="card h-100 bg-white border shadow-lg"
          style={{
            transition: "all 0.3s ease-in-out",
            flex: selectedPoints ? "1 1 50%" : "0 0 0%",
            transform: selectedPoints ? "scale(1)" : "scale(0.95)",
            transformOrigin: "left center",
            opacity: selectedPoints ? 1 : 0,
            overflow: "hidden",
          }}
        >
          <div className="d-flex flex-column h-100">
            <div className="position-relative">
              <button
                title="Copy pane to Clipboard - Not implemented"
                className="btn btn-sm btn-outline-primary position-absolute"
                style={{
                  top: "16px",
                  right: "16px",
                  zIndex: 10,
                  transition: "background 0.2s, color 0.2s",
                  background: "transparent",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "#dc3545";
                  (e.currentTarget as HTMLButtonElement).style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "transparent";
                  (e.currentTarget as HTMLButtonElement).style.color = "";
                }}
                onClick={() => {
                  console.log(
                    "Implementation guide: COPY_IMPLEMENTATION_GUIDE.md",
                  );
                }}
              >
                <i className="bi bi-copy"></i>
              </button>
            </div>
            {selectedPoints && (
              <div className="p-3 pe-5 d-inline-flex flex-column align-self-start">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Selected Pair Details</h5>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedPoints(null)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <div className="border rounded p-3 d-inline-flex gap-4 flex-wrap">
                  {/* Pair & Correlations */}
                  <div style={{ minWidth: 250 }}>
                    <table className="table table-sm table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td className="fw-semibold pe-2" style={{ width: 30 }}>X:</td>
                          <td className="fw-bold" style={{ width: 30 }}>{selectedPoints.x}</td>
                          <td className="text-muted small">
                            {rootLUT[selectedPoints.x as keyof typeof rootLUT]?.name}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-semibold pe-2">Y:</td>
                          <td className="fw-bold" style={{ width: 30 }}>{selectedPoints.y}</td>
                          <td className="text-muted small">
                            {rootLUT[selectedPoints.y as keyof typeof rootLUT]?.name}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {correlationData && (
                      <div className="mt-2 pt-2 border-top">
                        <div className="fw-bold mb-1">Correlations</div>
                        <div className="d-flex gap-3">
                          {correlationData.map((data, index) => (
                            <div key={index} className="text-center">
                              <div className="text-muted small">{data.days}d</div>
                              <div className="fw-semibold">
                                {data.z[data.x.indexOf(selectedPoints.x)][
                                  data.y.indexOf(selectedPoints.y)
                                ].toFixed(3)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ADR Ratio */}
                  <div className="border-start ps-4">
                    <div className="fw-bold mb-1">
                      ADR Ratio{" "}
                      <span className="text-muted" style={{ fontSize: "0.75em", opacity: 0.7 }}>({adrLastUpdate})</span>

                    </div>

                    {(() => {
                      const pair = adrRatioPairs?.data?.[selectedPoints.x]?.[selectedPoints.y];
                      if (!pair) return <span className="text-muted small">No ADR data</span>;
                      return (
                        <table className="table table-sm table-borderless mb-0">
                          <tbody>
                            <tr>
                              <td className="text-muted small">Ratio</td>
                              <td className="fw-semibold">{pair.ratio_str}</td>
                            </tr>
                            <tr>
                              <td className="text-muted small">Ideal</td>
                              <td className="fw-semibold">{pair.ideal_ratio_str}</td>
                            </tr>
                            <tr>
                              <td className="text-muted small">Error</td>
                              <td className="fw-semibold">{pair.abs_log_err.toFixed(4)}</td>
                            </tr>
                            <tr>
                              <td className="text-muted small">ZN Risk</td>
                              <td className="fw-semibold" style={{ minWidth: 120 }}>{pair.zn_risk}</td>
                            </tr>
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
            <div className="flex-grow-1  p-3" style={{ minHeight: 0 }}>
              <Plot
                className="w-100 h-100"
                data={
                  historicalCorrelationPair
                    ? Object.entries(
                        historicalCorrelationPair.reduce((acc, curr) => {
                          if (!acc[curr.averaging_days]) {
                            acc[curr.averaging_days] = [];
                          }
                          acc[curr.averaging_days].push(curr);
                          return acc;
                        }, {} as Record<number, typeof historicalCorrelationPair>),
                      ).map(([days, data]) => {
                        // Sort data by date before mapping
                        const sortedData = data.sort(
                          (a, b) =>
                            new Date(a.date).getTime() -
                            new Date(b.date).getTime(),
                        );
                        return {
                          type: "scatter",
                          mode: "lines+markers",
                          opacity: 0.8,
                          line: { shape: "hv", width: 3 },
                          name: `${days} day`,
                          x: sortedData.map(
                            (d) => new Date(new Date(d.date).toDateString()),
                          ),
                          y: sortedData.map((d) => d.correl),
                        };
                      })
                    : []
                }
                layout={{
                  title: `Historical Correlation for ${selectedPoints?.x} and ${selectedPoints?.y}`,
                  // margin: { t: 30, r: 10, l: 50, b: 30 },
                  autosize: true,
                  annotations: historicalCorrelationPair
                    ? [
                        // Single underlined annotation for the latest date
                        (() => {
                          const allData = historicalCorrelationPair.reduce(
                            (acc, curr) => {
                              if (!acc[curr.averaging_days]) {
                                acc[curr.averaging_days] = [];
                              }
                              acc[curr.averaging_days].push(curr);
                              return acc;
                            },
                            {} as Record<
                              number,
                              typeof historicalCorrelationPair
                            >,
                          );

                          const allDates = historicalCorrelationPair.map((d) =>
                            new Date(d.date).getTime(),
                          );
                          const latestDate = new Date(Math.max(...allDates));

                          // Get the highest correlation value from the latest points of each series
                          const latestCorrelationValues = Object.entries(
                            historicalCorrelationPair.reduce((acc, curr) => {
                              if (!acc[curr.averaging_days]) {
                                acc[curr.averaging_days] = [];
                              }
                              acc[curr.averaging_days].push(curr);
                              return acc;
                            }, {} as Record<number, typeof historicalCorrelationPair>),
                          ).map(([days, data]) => {
                            const sortedData = data.sort(
                              (a, b) =>
                                new Date(a.date).getTime() -
                                new Date(b.date).getTime(),
                            );
                            return sortedData[sortedData.length - 1].correl;
                          });

                          return {
                            x: new Date(latestDate.getTime()).toISOString(), // Shift 1 day to the right
                            y: Math.max(...latestCorrelationValues) + 0.05, // Just above the highest final point
                            text: `<u>${latestDate.toLocaleDateString()}</u>`,

                            font: {
                              size: 10,
                              color: "#636363",
                            },
                            showarrow: true,
                            arrowcolor: "rgb(255,255,255)",
                            arrowwidth: 0.5,
                            ax: 60,
                          };
                        })(),
                        // Individual series annotations
                        ...Object.entries(
                          historicalCorrelationPair.reduce((acc, curr) => {
                            if (!acc[curr.averaging_days]) {
                              acc[curr.averaging_days] = [];
                            }
                            acc[curr.averaging_days].push(curr);
                            return acc;
                          }, {} as Record<number, typeof historicalCorrelationPair>),
                        ).map(([days, data]) => {
                          // Sort data by date before mapping
                          const sortedData = data.sort(
                            (a, b) =>
                              new Date(a.date).getTime() -
                              new Date(b.date).getTime(),
                          );
                          const latestPoint = sortedData[sortedData.length - 1];
                          return {
                            x: new Date(
                              new Date(latestPoint.date).toDateString(),
                            ).toISOString(),
                            y: latestPoint.correl,
                            text: `${days}d: ${latestPoint.correl.toFixed(3)}`,
                            showarrow: true,
                            arrowhead: 2,
                            arrowsize: 1,
                            arrowwidth: 1.5,
                            arrowcolor: "#636363",
                            ax: 60,
                            ay: -10,
                            font: {
                              size: 10,
                              color: "#636363",
                            },
                          };
                        }),
                      ]
                    : [],
                }}
                config={{
                  responsive: true,
                  displayModeBar: true,
                }}
                useResizeHandler={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


export default Correlation;
